import { useCallback, useEffect, useState } from "react";
import { categories } from "@/mocks/products";
import { getMarketplaceOptions } from "@/hooks/useAdminMarketplaces";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";
import {
  fetchSupabaseCollection,
  loadCachedValue,
  replaceSupabaseCollection,
  saveCachedValue,
  subscribeToCachedValue,
} from "@/integrations/supabase/contentStore";

const STORAGE_KEY = "afiliabr_home_hero_slides";

export type HeroMarketplaceFilter = string;

export interface HeroSlide {
  id: number;
  imageUrl: string;
  categoryPath: string;
  categoryLabel: string;
  marketplace: HeroMarketplaceFilter;
}

type HeroSlideRow = {
  id: number | string;
  image_url: string;
  category_path: string;
  category_label: string;
  marketplace: string;
  display_order: number | string;
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function getHeroMarketplaceOptions() {
  return ["Todos", ...getMarketplaceOptions()];
}

export const defaultHeroSlides: HeroSlide[] = [
  {
    id: 1,
    imageUrl: categories[0].image,
    categoryPath: `/categoria/${categories[0].slug}`,
    categoryLabel: categories[0].name,
    marketplace: "Todos",
  },
  {
    id: 2,
    imageUrl: categories[1].image,
    categoryPath: `/categoria/${categories[1].slug}`,
    categoryLabel: categories[1].name,
    marketplace: "Todos",
  },
  {
    id: 3,
    imageUrl: categories[3].image,
    categoryPath: `/categoria/${categories[3].slug}`,
    categoryLabel: categories[3].name,
    marketplace: "Todos",
  },
];

function normalizeSlide(slide: Partial<HeroSlide>, index: number): HeroSlide {
  const marketplaceOptions = getHeroMarketplaceOptions();

  return {
    id: Number(slide.id ?? index + 1),
    imageUrl: slide.imageUrl?.trim() ?? "",
    categoryPath: slide.categoryPath?.trim() ?? "/categorias",
    categoryLabel: slide.categoryLabel?.trim() ?? "Categorias",
    marketplace: marketplaceOptions.includes(slide.marketplace ?? "") ? slide.marketplace ?? "Todos" : "Todos",
  };
}

function normalizeSlides(data: Partial<HeroSlide>[]) {
  return data.map((slide, index) => normalizeSlide(slide, index));
}

function mapRow(row: HeroSlideRow): HeroSlide {
  return normalizeSlide({
    id: Number(row.id),
    imageUrl: row.image_url,
    categoryPath: row.category_path,
    categoryLabel: row.category_label,
    marketplace: row.marketplace,
  }, Number(row.display_order ?? 0));
}

function mapToRow(slide: HeroSlide, index: number) {
  return {
    id: slide.id,
    image_url: slide.imageUrl,
    category_path: slide.categoryPath,
    category_label: slide.categoryLabel,
    marketplace: slide.marketplace,
    display_order: index,
  };
}

function loadSlides() {
  return normalizeSlides(loadCachedValue<HeroSlide[]>(STORAGE_KEY, defaultHeroSlides));
}

function persistSlides(slides: HeroSlide[]) {
  saveCachedValue(STORAGE_KEY, normalizeSlides(slides));
}

async function fetchFromSupabase() {
  const rows = await fetchSupabaseCollection<HeroSlideRow>("hero_slides", "display_order");
  return rows.map((row) => mapRow(row));
}

async function syncToSupabase(slides: HeroSlide[]) {
  const normalized = normalizeSlides(slides);
  const rows = await replaceSupabaseCollection({
    table: "hero_slides",
    rows: normalized.map((slide, index) => mapToRow(slide, index)),
    orderBy: "display_order",
  });

  return (rows as HeroSlideRow[]).map((row) => mapRow(row));
}

export function buildHeroDestination(path: string, marketplace: HeroMarketplaceFilter) {
  if (marketplace === "Todos") return path;
  return `${path}${path.includes("?") ? "&" : "?"}marketplace=${encodeURIComponent(marketplace)}`;
}

export function getHeroRouteOptions() {
  return [
    { label: "Todas as Categorias", path: "/categorias" },
    ...categories.flatMap((category) => [
      {
        label: category.name,
        path: `/categoria/${category.slug}`,
      },
      ...category.subcategories.map((subcategory) => ({
        label: `${category.name} / ${subcategory}`,
        path: `/categoria/${category.slug}/${slugify(subcategory)}`,
      })),
    ]),
  ];
}

export function useHeroSlides() {
  const { session } = useSessionContext();
  const [slides, setSlides] = useState<HeroSlide[]>(loadSlides);

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEY, defaultHeroSlides, (value) => {
      setSlides(normalizeSlides(value as HeroSlide[]));
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remote = await fetchFromSupabase();
        if (!active) return;

        if (remote.length > 0) {
          setSlides(remote);
          persistSlides(remote);
          return;
        }

        const cached = loadSlides();
        if (!session || cached.length === 0) return;

        const seeded = await syncToSupabase(cached);
        if (!active) return;

        setSlides(seeded);
        persistSlides(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const saveSlides = useCallback((nextSlides: HeroSlide[]) => {
    const normalized = normalizeSlides(nextSlides);
    setSlides(normalized);
    persistSlides(normalized);

    void syncToSupabase(normalized).then((remote) => {
      setSlides(remote);
      persistSlides(remote);
    }).catch(() => {
      // ignore and keep cache fallback
    });
  }, []);

  const addSlide = useCallback((slide: Omit<HeroSlide, "id">) => {
    const nextId = Math.max(0, ...slides.map((item) => item.id)) + 1;
    saveSlides([...slides, { ...slide, id: nextId }]);
  }, [saveSlides, slides]);

  const updateSlide = useCallback((id: number, data: Omit<HeroSlide, "id">) => {
    saveSlides(slides.map((slide) => (slide.id === id ? { ...data, id } : slide)));
  }, [saveSlides, slides]);

  const deleteSlide = useCallback((id: number) => {
    const nextSlides = slides.filter((slide) => slide.id !== id);
    saveSlides(nextSlides.length > 0 ? nextSlides : defaultHeroSlides);
  }, [saveSlides, slides]);

  return {
    slides,
    addSlide,
    updateSlide,
    deleteSlide,
    routeOptions: getHeroRouteOptions(),
    marketplaceOptions: getHeroMarketplaceOptions(),
  };
}
